import { create } from "zustand";
import actions from "../config.json";

// Map API level names to display names
const TIER_DISPLAY_NAMES = {
  MEMBER: "Member",
  SILVER: "Silver Elite",
  GOLD: "Gold Elite",
  PLATINUM: "Platinum Elite",
  TITANIUM: "Titanium Elite",
  AMBASSADOR: "Ambassador Elite",
};

// Nights required for each tier
const TIER_NIGHT_REQUIREMENTS = {
  MEMBER: 0,
  SILVER: 10,
  GOLD: 25,
  PLATINUM: 50,
  TITANIUM: 75,
  AMBASSADOR: 100,
};

export const useProfileStore = create((set, get) => ({
  profile: null,
  loading: false,
  error: null,
  lastFetchedAcid: null,

  fetchProfile: async (acid) => {
    const { lastFetchedAcid, loading, profile } = get();

    // Avoid refetching if we already have data for this acid or are currently loading it
    if (acid === lastFetchedAcid && (profile || loading)) {
      return;
    }

    set({ loading: true, error: null, lastFetchedAcid: acid });

    try {
      const actionUrl = actions["marriott-passions-v3/get-profile"];
      const response = await fetch(actionUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ acid }),
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      const profileData = result.body !== undefined ? result.body : result;
      console.log("CDP profile response:", result.cdpResponse); // Debugging log

      set({ profile: profileData, loading: false });
    } catch (err) {
      console.error("Error loading profile:", err);
      set({ error: err.message, loading: false });
    }
  },

  // Selectors for membership tier data
  getMembershipTierData: () => {
    const { profile } = get();
    if (!profile) {
      return null;
    }

    const bonvoyLevel = String(profile.bonvoyLevel || "MEMBER").toUpperCase();
    const nextLevel = profile.nextLevel
      ? String(profile.nextLevel).toUpperCase()
      : null;

    const currentNights = profile.currentNights || 0;
    const nightsToNextLevel = profile.nightsToNextLevel || 0;
    const targetNights = nextLevel
      ? TIER_NIGHT_REQUIREMENTS[nextLevel] || currentNights + nightsToNextLevel
      : currentNights;

    return {
      tierName: TIER_DISPLAY_NAMES[bonvoyLevel] || "Member",
      nextTierName: nextLevel ? TIER_DISPLAY_NAMES[nextLevel] : null,
      currentProgress: currentNights,
      targetProgress: targetNights,
      nightsToNextLevel,
      bonvoyLevel,
      nextLevel,
    };
  },

  getLastSearchDestination: () => {
    const { profile } = get();
    return profile?.lastSearchDestination || null;
  },

  /**
   * Returns whether the user has opted OUT of personalization.
   */
  getOptedOutPersonalization: () => {
    const { profile } = get();
    return profile?.optedOutPersonalization ?? false;
  },

  /**
   * Returns whether to show the consent modal.
   * True only when user previously opted out (value="y" in CDP).
   */
  shouldShowConsentModal: () => {
    const { profile } = get();
    return profile?.showConsentModal ?? false;
  },

  getPassions: () => {
    const { profile } = get();
    return profile?.passions || [];
  },
}));
